import { useChat } from '@ai-sdk/react';
import { isToolUIPart, lastAssistantMessageIsCompleteWithToolCalls, type UIMessage } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildChatTransport, fetchMessages, HandshakeError, type AgentMeta } from '../api';
import type { ToolDefinition } from '../parent-bridge';
import type { EmbedStrings } from '../strings';
import { ToolBridge } from '../tool-bridge';
import { Composer } from './Composer';
import { MessageBubble, type PendingConfirm } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';

// Sentinel emitted by the backend's widget-stream-filter for cadastered tools.
// Source of truth lives at packages/agents/functions/services/widget-stream-filter.ts
// (PLATFORM_TOOL_NAME). Cross-package import isn't possible (server vs browser
// bundle); the wire schema rejects this exact name at registration so collision
// is impossible — only this duplicated literal stays in sync by code review.
const PLATFORM_TOOL_NAME = 'platform_tool';

type Props = {
  session: { token: string; agent: AgentMeta };
  tools: ToolDefinition[];
  parentOrigin: string;
  strings: EmbedStrings;
  onCloseRequest: () => void;
  onSessionExpired: () => void;
  /** Read-on-demand getter for the host's current liveContext. Called per
   *  chat send by the transport. */
  getLiveContext?: () => string | null | undefined;
};

export function ChatPanel({
  session,
  tools,
  parentOrigin,
  strings,
  onCloseRequest,
  onSessionExpired,
  getLiveContext,
}: Props) {
  // turnId is generated synchronously in the Composer onSend callback, BEFORE
  // useChat fires `prepareSendMessagesRequest`. Held in a ref so the transport
  // can read the latest value without re-rendering on every keystroke. Same
  // value is reused across the user-msg → toolResults loop within one turn,
  // and reset on the next user submit.
  const currentTurnIdRef = useRef<string | null>(null);

  // Transport is bound to the current sessionToken — re-create only when the
  // token changes (post-rehandshake). useMemo on token + parentOrigin.
  // getLiveContext is read on-demand per send; keep a ref so the transport
  // closure captures the latest function without forcing a re-memo (which
  // would tear down the chat stream on every render).
  const getLiveContextRef = useRef(getLiveContext);
  useEffect(() => {
    getLiveContextRef.current = getLiveContext;
  }, [getLiveContext]);

  // Tools come from parent (loader push) and travel inline in the chat body.
  // Stash in a ref so transport reads on-demand without re-memoing whenever
  // the host changes tools mid-session (rare; live-update path).
  const toolsRef = useRef(tools);
  useEffect(() => {
    toolsRef.current = tools;
  }, [tools]);

  const transport = useMemo(
    () =>
      buildChatTransport({
        sessionToken: session.token,
        parentOrigin,
        getCurrentTurnId: () => currentTurnIdRef.current,
        getLiveContext: () => getLiveContextRef.current?.() ?? null,
        getTools: () => toolsRef.current,
      }),
    [session.token, parentOrigin],
  );

  // ToolBridge lifecycle: one bridge per ChatPanel instance, keyed on the
  // pinned parentOrigin. destroy() resolves any in-flight Promises so the
  // useChat onToolCall callback can fall through cleanly to addToolOutput.
  const bridgeRef = useRef<ToolBridge | null>(null);
  useEffect(() => {
    const bridge = new ToolBridge(parentOrigin);
    bridgeRef.current = bridge;
    return () => {
      bridge.destroy();
      bridgeRef.current = null;
    };
  }, [parentOrigin]);

  // Lookup table for tool metadata at call time. Tools come from the parent
  // via init payload; if the host updates them mid-session, the new map
  // applies on the next render.
  const toolsByName = useMemo(() => {
    const m = new Map<string, ToolDefinition>();
    for (const t of tools) m.set(t.name, t);
    return m;
  }, [tools]);

  // Pending user-confirmations keyed by toolCallId. Lives alongside the
  // ai-sdk message state so render can correlate by toolCallId.
  const [pendingConfirms, setPendingConfirms] = useState<Map<string, PendingConfirm>>(
    () => new Map(),
  );
  const setPending = useCallback(
    (toolCallId: string, value: PendingConfirm | null) => {
      setPendingConfirms((prev) => {
        const next = new Map(prev);
        if (value === null) next.delete(toolCallId);
        else next.set(toolCallId, value);
        return next;
      });
    },
    [],
  );

  // Wait for the user to click Confirm or Cancel on a tool's confirm card.
  // Resolves with the decision; the caller decides what to do next.
  const awaitUserDecision = useCallback(
    (params: {
      toolCallId: string;
      name: string;
      description?: string;
      inputSchema?: { properties?: Record<string, { description?: string; title?: string }> };
      input: unknown;
    }) =>
      new Promise<'confirm' | 'cancel'>((resolve) => {
        setPending(params.toolCallId, {
          name: params.name,
          description: params.description,
          inputSchema: params.inputSchema,
          input: params.input,
          resolve: (decision) => {
            setPending(params.toolCallId, null);
            resolve(decision);
          },
        });
      }),
    [setPending],
  );

  const { messages, setMessages, sendMessage, status, error, addToolOutput, stop } = useChat<UIMessage>({
    transport,
    onError: (err) => {
      if (err instanceof HandshakeError && err.status === 401) {
        onSessionExpired();
      }
    },
    onToolCall: async ({ toolCall }) => {
      const bridge = bridgeRef.current;
      if (!bridge) return;
      const toolName = toolCall.toolName;

      // Platform tools (KB retrieval, calculator, http_fetch, web_search, ...)
      // run server-side and emit `tool-output-available` from the same stream.
      // widget-stream-filter renames them to this reserved token — same string
      // is rejected at registration (widget/chat.ts ToolDefinitionSchema) so a
      // legitimate client tool can't collide with it.
      if (toolName === PLATFORM_TOOL_NAME) return;

      const meta = toolsByName.get(toolName);

      // IMPORTANTE: addToolOutput NÃO PODE ser awaited aqui.
      //
      // AI SDK usa um SerialJobExecutor (FIFO). O stream da resposta atual
      // está sendo processado por um job em execução; awaitar `addToolOutput`
      // (que enfileira um novo job) deadlocka — o job novo só roda após o
      // stream's job terminar, mas o stream's job está bloqueado em
      // `await onToolCall(...)`.
      //
      // Solução: fire-and-forget. O job enfileirado roda depois que o stream
      // termina (status='ready'); aí dentro de addToolOutput o próprio AI SDK
      // dispara `sendAutomaticallyWhen` (linha ~13049 de ai/dist/index.js)
      // pra continuar o turn com os toolResults. Awaitar antes disso trava
      // o pipeline indefinidamente — UI fica em "Refletindo…" pra sempre.

      // Tool not in the session snapshot. Could happen if the LLM
      // hallucinates a tool name or if the tool was deleted mid-session.
      // Fail fast so the LLM gets feedback and can recover.
      if (!meta) {
        void addToolOutput({
          tool: toolName,
          toolCallId: toolCall.toolCallId,
          state: 'output-error',
          errorText: `tool "${toolName}" is not registered for this session`,
        });
        return;
      }

      if (!meta.autoRun) {
        const decision = await awaitUserDecision({
          toolCallId: toolCall.toolCallId,
          name: toolName,
          description: meta.description,
          inputSchema: meta.inputSchema as {
            properties?: Record<string, { description?: string; title?: string }>;
          },
          input: toolCall.input,
        });
        if (decision === 'cancel') {
          // Structured "user denied" signal (NOT errorText). The LLM gets a
          // clean output it can interpret as "the user explicitly declined";
          // the message bubble renders this as "ação cancelada" instead of
          // a failure pill, and the LLM is less likely to nag for a retry.
          void addToolOutput({
            tool: toolName,
            toolCallId: toolCall.toolCallId,
            output: { _denied: true },
          });
          return;
        }
      }

      const result = await bridge.requestToolCall({
        toolCallId: toolCall.toolCallId,
        name: toolName,
        input: toolCall.input,
        autoRun: meta.autoRun,
      });

      if ('error' in result) {
        void addToolOutput({
          tool: toolName,
          toolCallId: toolCall.toolCallId,
          state: 'output-error',
          errorText: result.error,
        });
      } else {
        void addToolOutput({
          tool: toolName,
          toolCallId: toolCall.toolCallId,
          output: result.output,
        });
      }
    },
    // After every tool result lands, AI SDK checks whether the message is now
    // "complete with tool calls" (all calls have a result) — if so, it auto-
    // resubmits to the model so the LLM can keep going. Without this, the
    // chat would freeze after the first tool result.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
  });

  // A conversa foi escalada pra um humano. O atendimento é assíncrono — o
  // operador pode estar offline; a resposta chega ao reabrir o widget
  // (continuidade) ou via polling se ele responder enquanto a sessão segue aberta.
  const [humanMode, setHumanMode] = useState(false);

  // Liga o modo human a cada NOVA part `data-escalated` (backend emite no SSE).
  // Contar (em vez de "tem alguma") deixa o polling desligar via assistantMode
  // no return-to-agent sem este efeito reativar — e religa numa re-escalação.
  const escalatedSeenRef = useRef(0);
  useEffect(() => {
    const count = messages.filter(hasEscalatedPart).length;
    if (count > escalatedSeenRef.current) {
      escalatedSeenRef.current = count;
      setHumanMode(true);
    }
  }, [messages]);

  // Busca a janela do servidor e mescla por id. `onAgent` controla a assimetria:
  // no mount nunca derruba o modo human (a sinalização pode ter acabado de
  // chegar); no polling, return-to-agent (assistantMode != human) desliga.
  const applyServerWindow = useCallback(
    async (onAgent: 'keep' | 'clear') => {
      const res = await fetchMessages(session.token);
      if (res.items.length > 0) setMessages((cur) => mergeMessagesById(cur, res.items));
      if (res.assistantMode === 'human') setHumanMode(true);
      else if (onAgent === 'clear') setHumanMode(false);
    },
    [session.token, setMessages],
  );

  // Carrega o histórico no mount (plano-10): numa thread contínua, o cliente
  // reabre e vê a conversa anterior — incluindo a resposta humana que chegou
  // enquanto fechado. Thread nova (FAQ/per-session) devolve vazio (no-op). Se a
  // thread está em modo human, liga o estado (banner + polling) — `data-escalated`
  // não é persistido, então o reabrir depende do `assistantMode` do endpoint.
  const historyLoadedRef = useRef(false);
  useEffect(() => {
    if (historyLoadedRef.current) return;
    historyLoadedRef.current = true;
    // sem histórico / transitório — segue com o chat vazio.
    applyServerWindow('keep').catch(() => {});
  }, [applyServerWindow]);

  // Polling (bônus): enquanto em modo human, busca a janela e mescla por id.
  // O caminho principal do suporte assíncrono é ver a resposta ao reabrir.
  useEffect(() => {
    if (!humanMode) return;
    let cancelled = false;
    const id = setInterval(() => {
      if (cancelled) return;
      // transitório (rede / 401 re-handshake) — próximo tick tenta de novo.
      applyServerWindow('clear').catch(() => {});
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [humanMode, applyServerWindow]);

  // A mensagem-sinal só carrega a part `data-escalated` (sem texto/tool) — não
  // renderiza bolha. Filtra pra não mostrar um balão vazio.
  const visibleMessages = useMemo(
    () => messages.filter((m) => m.parts.some((p) => p.type === 'text' || isToolUIPart(p))),
    [messages],
  );

  const listEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // jsdom (tests) doesn't implement scrollIntoView. Guard so the effect
    // doesn't crash the tree on first paint.
    listEndRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [messages, status]);

  // Distinguish "model is producing tokens" from "model is paused waiting on
  // user confirmation". The typing indicator only makes sense in the first
  // case — showing "…" while the LLM is waiting on user input misrepresents
  // the state.
  //
  // Composer stays enabled in BOTH cases: the user can interrupt an in-flight
  // response by sending a new message (we abort the stream first, see
  // handleSend below) and can also add context while a tool-confirm is open.
  const awaitingConfirm = pendingConfirms.size > 0;
  const isModelStreaming = status === 'streaming' || status === 'submitted';
  const showTypingIndicator = isModelStreaming && !awaitingConfirm;

  // Interrupt-and-replace: if the model is mid-stream when the user sends a
  // new message, abort the in-flight request first. AI SDK's `stop()` fires
  // the request's AbortController — the fetch is cancelled, no further
  // events from that stream are processed, and the backend sees the client
  // disconnect (chat.ts wires `responseStream.on('close')` to abort the
  // streamText, so we don't burn tokens on a response nobody will read).
  const handleSend = useCallback(
    (text: string) => {
      if (isModelStreaming) stop();
      // Fresh turn → fresh turnId. Tool-result resubmits within the same
      // turn reuse this same id (the transport reads from the ref each
      // call). UUIDv4 is enough; the server only checks string match,
      // not entropy.
      currentTurnIdRef.current = generateTurnId();
      void sendMessage({ text });
    },
    [isModelStreaming, stop, sendMessage],
  );

  return (
    <div className="kb-panel">
      <header className="kb-panel__header">
        {/* Empty spacer (flex:1) — agent slug/description can leak ops detail
            to end users, so header stays neutral until we add a display field. */}
        <div className="kb-panel__heading" />
        <button
          type="button"
          className="kb-panel__close"
          aria-label={strings.closeLabel}
          onClick={onCloseRequest}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M3 3l10 10M13 3L3 13" />
          </svg>
        </button>
      </header>

      <div className="kb-panel__messages" role="log" aria-live="polite">
        {visibleMessages.length === 0 && !humanMode ? (
          <div className="kb-empty">
            <p>{strings.emptyPrompt}</p>
          </div>
        ) : (
          visibleMessages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              pendingConfirms={pendingConfirms}
              strings={strings}
            />
          ))
        )}
        {showTypingIndicator ? (
          <TypingIndicator
            verbsCsv={strings.thinkingVerbs}
            timerSuffix={strings.thinkingTimerSuffix}
          />
        ) : null}
        <div ref={listEndRef} aria-hidden="true" />
      </div>

      {humanMode ? (
        <div className="kb-panel__human-notice" role="status" aria-live="assertive">
          {strings.humanModeNotice}
        </div>
      ) : null}

      {error && !(error instanceof HandshakeError && error.status === 401) ? (
        <div className="kb-panel__error" role="alert">
          {labelForError(error, strings)}
        </div>
      ) : null}

      <Composer
        disabled={awaitingConfirm}
        placeholder={strings.composerPlaceholder}
        ariaLabel={strings.composerLabel}
        sendLabel={strings.composerSendLabel}
        onSend={handleSend}
      />
    </div>
  );
}

/**
 * UUIDv4 if available (modern browsers + jsdom 22+), otherwise a 24-char
 * timestamp-prefixed random fallback (collision-resistant for the lifetime
 * of a session). The server treats turnId as opaque — only equality matters.
 */
function generateTurnId(): string {
  const c = (globalThis.crypto as { randomUUID?: () => string }) ?? {};
  if (typeof c.randomUUID === 'function') return c.randomUUID();
  const rand = Math.random().toString(36).slice(2, 14);
  return `t-${Date.now().toString(36)}-${rand}`;
}

/**
 * A thread entra em modo human quando o backend emite uma part `data-escalated`
 * no stream (widget/chat.ts) ou quando o histórico carregado no retorno traz
 * essa sinalização. Detecção pura sobre as mensagens do useChat.
 */
/** Uma mensagem carrega a sinalização de escalação (`data-escalated`) emitida
 *  no SSE pelo backend. Magic-string num único lugar. */
export function hasEscalatedPart(m: UIMessage): boolean {
  return m.parts.some((p) => (p as { type?: string }).type === 'data-escalated');
}

export function messagesIndicateHumanMode(messages: UIMessage[]): boolean {
  return messages.some(hasEscalatedPart);
}

/**
 * Mescla a janela do servidor no estado do useChat por id da mensagem
 * (`${turnSeq}-${role}`, estável). Atualiza as existentes com a versão do
 * servidor (mais fresca), anexa as novas no fim e PRESERVA mensagens só-cliente
 * (ex.: a sinalização `data-escalated`, que nunca é persistida) — sem isso o
 * polling derrubaria o modo human. Dedupe por id evita reentrega dupla.
 */
export function mergeMessagesById(current: UIMessage[], incoming: UIMessage[]): UIMessage[] {
  const incomingById = new Map(incoming.map((m) => [m.id, m]));
  const merged = current.map((m) => incomingById.get(m.id) ?? m);
  const currentIds = new Set(current.map((m) => m.id));
  for (const m of incoming) {
    if (!currentIds.has(m.id)) merged.push(m);
  }
  return merged;
}

function labelForError(err: Error, strings: EmbedStrings): string {
  if (err instanceof HandshakeError) {
    if (err.code === 'rate_limited') return strings.errorRateLimited;
    if (err.code === 'origin_mismatch') return strings.errorOriginMismatch;
  }
  return strings.errorGeneric;
}
